import { isEqual } from 'lodash-es'
import { useCallback, useMemo, useRef, useState } from 'react'

import {
  type Answers,
  initialValues,
  type ScreenErrors,
  type ScreenField,
  type ScreenValue,
  type ScreenValues,
  summarize,
  toPayload,
  validate as validateScreen,
} from './screenForm'

export interface ScreenForm {
  fields: ScreenField[]
  values: ScreenValues
  setValue: (key: string, value: ScreenValue) => void
  errors: ScreenErrors
  /** Replaces the errors, e.g. with the server's, keyed by top-level field key. */
  setErrors: (errors: ScreenErrors) => void
  clearError: (key: string) => void
  /** Checks every visible field, shows the errors and moves to the first; true when there are none. */
  validate: () => boolean
  payload: () => Answers
  summary: () => string
  isDirty: boolean
  reset: () => void
  /** Attach to the element wrapping the fields, so validate() can bring the first error into view. */
  containerRef: React.RefObject<HTMLDivElement | null>
}

/** State for one screen's fields, starting from the values the server sent; a new set of fields starts over. */
export function useScreenForm(fields: ScreenField[]): ScreenForm {
  const serializedFields = JSON.stringify(fields)
  const initial = useMemo(() => initialValues(fields), [serializedFields]) // eslint-disable-line react-hooks/exhaustive-deps
  const [values, setValues] = useState(initial)
  const [errors, setErrors] = useState<ScreenErrors>({})
  const [startedFrom, setStartedFrom] = useState(initial)
  const containerRef = useRef<HTMLDivElement | null>(null)

  // The server re-sent the screen (or the caller passed new fields): start from its values.
  if (startedFrom !== initial) {
    setStartedFrom(initial)
    setValues(initial)
    setErrors({})
  }

  const clearError = useCallback((key: string) => {
    setErrors((current) => {
      if (!(key in current)) return current
      const rest = { ...current }
      delete rest[key]
      return rest
    })
  }, [])

  const setValue = useCallback(
    (key: string, value: ScreenValue) => {
      setValues((current) => ({ ...current, [key]: value }))
      clearError(key)
    },
    [clearError]
  )

  const validate = useCallback(() => {
    const found = validateScreen(fields, values)
    setErrors(found)
    if (Object.keys(found).length === 0) return true
    requestAnimationFrame(() => {
      const first = containerRef.current?.querySelector('[data-screen-error]')
      if (!first) return
      first.scrollIntoView({ block: 'center', behavior: 'smooth' })
      first
        .querySelector<HTMLElement>('input:not([disabled]), textarea, button:not([disabled]), [tabindex="0"]')
        ?.focus({ preventScroll: true })
    })
    return false
  }, [fields, values])

  return {
    fields,
    values,
    setValue,
    errors,
    setErrors,
    clearError,
    validate,
    payload: () => toPayload(fields, values),
    summary: () => summarize(fields, values),
    isDirty: !isEqual(values, initial),
    reset: () => {
      setValues(initial)
      setErrors({})
    },
    containerRef,
  }
}
